<template>
  <v-container>
    <div class="elevation-2 px-5 mt-3">
      <SimplePageHeader title="Time Management" class="px-4 pr-5 mb-5">
        <template v-slot:right-buttons>
          <ButtonCustomIcon
            icon="cloud_download"
            color="primary"
            text="Excel"
            class="hide-on-print"
            @click="handleExportToExcel"
            :block="true"
          />
        </template>
      </SimplePageHeader>
      <v-row
        align="center"
        justify="start"
        class="px-5 mb-2 pb-3"
      >
        <v-col cols="12" sm="5" md="4" lg="3">
          <WeekPicker
            :hideDetails="true"
            :date="dateFilter"
            @weekChange="weekChangeHandler"
            @dateChange="(newValue) => this.dateFilter = newValue"
          ></WeekPicker>
        </v-col>
        <v-col cols="12" sm="6" md="3"
          v-if="clientsList && clientsList.length > 0"
          >
          <v-select
            label="Client"
            :items="clientsList"
            item-value="clientId"
            item-text="clientName"
            v-model="clientFilter"
            clearable
            outlined
            hide-details
          ></v-select>
        </v-col>
        <v-col cols="12" sm="6" md="3"
          v-if="consultantList && consultantList.length > 0"
          >
          <v-select
            label="Consultant"
            :items="consultantList"
            item-value="consultantId"
            item-text="consultantName"
            v-model="consultantFilter"
            clearable
            outlined
            hide-details
          ></v-select>
        </v-col>
      </v-row>
    </div>
    <v-row class="pt-2">
      <v-col cols="12">
        <v-card style="max-height: 500px; overflow-y: auto; overflow-x: hidden;">
          <v-card-text>
            <div
              class="px-4 pt-5"
            >
              <SimplePageHeader title="Consultants"></SimplePageHeader>
              <v-row class="hide-on-print">
                <v-col cols="12" align="right">
                  <ToggleButton
                    :state="collapseButtonState"
                    color="primary"
                    :statesLabel="collapseButtonLabels"
                    @click="(state) => this.collapseButtonState = state === 1 ? 2 : 1 "
                  ></ToggleButton>
                </v-col>
              </v-row>
              <v-data-table
                dense
                :headers="mainHeaders"
                :items="assignmentsTimeSheetEntriesValue"
                item-key="clientId"
                :sort-by="['clientName']"
                :sort-desc="[false]"
                hide-default-footer
                show-expand
                :expanded="expanded"
                :items-per-page="-1"
              >
                <template v-slot:expanded-item="{ headers, item }">
                  <td class="py-5" :colspan="headers.length">
                    <v-data-table
                      dense
                      :headers="subHeaders"
                      :items="item.assignmentsTimeSheetEntries"
                      class="pb-3 px-5"
                    >
                      <template v-for="(slot,i) of subHeaders" v-slot:[`item.${slot.value}`]="{ item }">
                        <span
                          :key="i"
                          v-if="i === 0 || i === 1" >
                          {{ item[`${slot.value}`] }}
                          <span v-if="i === 0" style="color:#808080;">
                            <br>
                            {{ item['totalTime'] | formatDecimal }} hours
                          </span>
                        </span>
                        <div
                          :key="i"
                          v-else
                        >
                          <span v-if="item[`${slot.value}`].hours === null || item[`${slot.value}`].hours === undefined"></span>
                          <FormattedNumberField
                            v-else
                            class="my-3"
                            :value="item[`${slot.value}`].hours"
                            :required="false"
                            :dense="true"
                            :validateOnBlur="true"
                            :hideDetails="true"
                            @valueChange="
                              timeValueChangeHandler(
                                item,
                                $event,
                                slot.value,
                                item.assignmentId,
                                item.consultantId,
                                null
                              )"
                          ></FormattedNumberField>
                        </div>
                      </template>
                    </v-data-table>
                  </td>
                </template>
                <template v-slot:item.totalTime="{ item }">
                  {{ item.totalTime | formatDecimal }} hours
                </template>
              </v-data-table>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <v-row v-if="staffTimeManagementItems != null && staffTimeManagementItems.length > 0">
      <v-col cols="12">
        <v-card style="max-height: 500px; overflow-y: auto; overflow-x: hidden;">
          <v-card-text>
            <div
              class="px-4 pt-5"
            >
              <SimplePageHeader title="Internal Employees"></SimplePageHeader>
              <v-container fluid class="px-0 pt-2 mt-4">
                <v-data-table
                  dense
                  :headers="staffHeaders"
                  :items="staffTimeManagementItems"
                  class="pb-3 px-5"
                >
                  <template v-for="(slot,i) of staffHeaders" v-slot:[`item.${slot.value}`]="{ item }">
                    <span
                      :key="i"
                      v-if="i === 0" >
                      {{ item[`${slot.value}`] }}
                      <span style="color:#808080;">
                        <br>
                        {{ item['totalTime'] | formatDecimal }} hours
                      </span>
                    </span>
                    <div
                      :key="i"
                      v-else
                    >
                      <span v-if="item[`${slot.value}`].hours === null || item[`${slot.value}`].hours === undefined"></span>
                      <FormattedNumberField
                        v-else
                        class="my-3"
                        :value="item[`${slot.value}`].hours"
                        :required="false"
                        :dense="true"
                        :validateOnBlur="true"
                        :hideDetails="true"
                        @valueChange="
                          timeValueChangeHandler(
                            item,
                            $event,
                            slot.value,
                            null,
                            item.userId,
                            item.staffId
                          )"
                      ></FormattedNumberField>
                    </div>
                  </template>
                </v-data-table>
              </v-container>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <ButtonsSubmitForm
      v-if="showSaveChangesButton"
      :isFormValid="true"
      acceptLabel="Save Changes"
      cancelLabel="Discard"
      @save="saveChanges"
      @cancel="discardChanges"
    ></ButtonsSubmitForm>
    <div
      v-if="showSaveChangesButton"
      class="hide-on-md hide-on-print mt-5 abs-left-0"
    >
      <v-container fluid class="fixed-buttons mt-0 px-0">
        <v-layout row>
          <v-flex xs6 md2 class="px-5 py-3">
            <v-btn
              block
              depressed
              color="primary"
              @click="saveChanges"
              >
              Save Changes
            </v-btn>
          </v-flex>
          <v-flex xs6 md2 class="px-5 py-3">
            <v-btn
              block
              depressed
              outlined
              @click="discardChanges"
              >
              Discard
            </v-btn>
          </v-flex>
        </v-layout>
      </v-container>
    </div>
    <BottomSpacer></BottomSpacer>
  </v-container>
</template>

<script>
import { SimplePageHeader } from '@/components/pageComponents'
import { mapFields } from '@/vueHelpers/vuex'
import { mapActions } from 'vuex'
import { BottomSpacer, ToggleButton, ButtonCustomIcon, ButtonsSubmitForm } from '@/components/buttons'
import { WeekPicker } from '@/components/shared/datePickers'
import { FormattedNumberField } from '@/components/shared/textFields'
import excel from '@/lib/export/excel'
import { formatDate } from '@/lib/display'

export default {
  components: {
    SimplePageHeader,
    BottomSpacer,
    WeekPicker,
    ToggleButton,
    ButtonCustomIcon,
    FormattedNumberField,
    ButtonsSubmitForm
  },
  data () {
    return {
      dateFilter: null,
      dateBeginFilter: null,
      dateEndFilter: null,
      mainHeaders: [
        { text: 'Client', value: 'clientName', sortable: false, cellClass: 'py-5' },
        { text: 'Total Hours', value: 'totalTime', sortable: false, cellClass: 'py-5', align: 'right' }
      ],
      collapseButtonLabels: {
        1: 'Contract All',
        2: 'Expand All'
      },
      collapseButtonState: 1, // 1 - Expanded, 2 - Collapsed
      clientsList: [],
      clientFilter: null,
      consultantList: [],
      consultantFilter: null,
      timeSheetEntriesToSave: []
    }
  },
  computed: {
    ...mapFields('timeManagement', [
      'timeManagementClientItems',
      'staffTimeManagementItems'
    ]),
    showSaveChangesButton () {
      return this.timeSheetEntriesToSave && this.timeSheetEntriesToSave.length > 0
    },
    currentWeek () {
      return this.dateFilter >= new Date(this.dateBeginFilter) && this.dateFilter <= new Date(this.dateEndFilter)
    },
    subHeaders: {
      get () {
        let headers = [
          { text: 'Consultant', value: 'consultantName', sortable: true, width: '160px', class: 'px-1', cellClass: 'px-1' },
          { text: 'Job Title', value: 'jobTitle', sortable: true, width: '130px', class: 'px-1', cellClass: 'px-1' }
        ]
        for (const item in this.timeManagementClientItems[0].assignmentsTimeSheetEntries[0]) {
          headers.push({
            text: item,
            value: item,
            align: 'center',
            sortable: false,
            width: '15px',
            cellClass: 'px-1'
          })
        }
        return headers.filter(
          header => header.text !== 'assignmentId' &&
            header.text !== 'clientId' &&
            header.text !== 'clientName' &&
            header.text !== 'consultantId' &&
            header.text !== 'consultantName' &&
            header.text !== 'jobTitle' &&
            header.text !== 'totalTime'
        )
      }
    },
    staffHeaders: {
      get () {
        let headers = [
          { text: 'Employee', value: 'employeeName', sortable: true, width: '160px', class: 'px-1', cellClass: 'px-1' }
        ]
        for (const item in this.staffTimeManagementItems[0]) {
          headers.push({
            text: item,
            value: item,
            align: 'center',
            sortable: false,
            width: '15px',
            cellClass: 'px-1'
          })
        }
        return headers.filter(
          header => header.text !== 'staffId' &&
            header.text !== 'employeeName' &&
            header.text !== 'totalTime' &&
            header.text !== 'userId'
        )
      }
    },
    expanded: {
      get () {
        if (this.collapseButtonState === 1) {
          return this.assignmentsTimeSheetEntriesValue || []
        } else {
          return []
        }
      }
    },
    assignmentsTimeSheetEntriesValue () {
      let itemsToShow = this.timeManagementClientItems
      if (this.clientFilter) {
        itemsToShow = itemsToShow.filter(client => client.clientId === this.clientFilter)
      }
      if (this.consultantFilter) {
        itemsToShow = itemsToShow.map(client => {
          let tempClient = JSON.parse(JSON.stringify(client))
          tempClient.assignmentsTimeSheetEntries = tempClient.assignmentsTimeSheetEntries.filter(
            assignment => assignment.consultantId === this.consultantFilter
          )
          if (tempClient.assignmentsTimeSheetEntries.length > 0) {
            return tempClient
          }
        })
        itemsToShow = itemsToShow.filter(client => client !== null && client !== undefined)
      }
      return itemsToShow
    }
  },
  methods: {
    ...mapActions('timeManagement', [
      'getTimeManagementData',
      'getStaffTimeManagementData',
      'saveTimeSheetEntries'
    ]),
    ...mapActions('datetimeHelper', [
      'getStaffLynxTime',
      'getUTCTime'
    ]),
    loadTime (dateBegin, dateEnd) {
      this.getTimeManagementData({ staffLynxDateBegin: dateBegin, staffLynxDateEnd: dateEnd }).then((timeManagementClientItems) => {
        this.clientsList = timeManagementClientItems.map(client => {
          return {
            clientId: client.clientId,
            clientName: client.clientName
          }
        }).sort((a, b) => (a.clientName > b.clientName) ? 1 : -1)
        timeManagementClientItems.map(client => {
          return client.assignmentsTimeSheetEntries.map(assignment => {
            this.consultantList.push({
              consultantId: assignment.consultantId,
              consultantName: assignment.consultantName
            })
          })
        })
        this.consultantList.sort((a, b) => (a.consultantName > b.consultantName) ? 1 : -1)

        this.getStaffTimeManagementData({ staffLynxDateBegin: dateBegin, staffLynxDateEnd: dateEnd }).then((staffTimeManagementItems) => {
          this.staffTimeManagementItems = staffTimeManagementItems
          this.staffTimeManagementItems.sort((a, b) => (a.employeeName > b.employeeName) ? 1 : -1)
        })
      })
      this.timeSheetEntriesToSave = []
    },
    weekChangeHandler (dateBegin, dateEnd) {
      this.dateBeginFilter = dateBegin
      this.dateEndFilter = dateEnd
      this.loadTime(dateBegin, dateEnd)
    },
    handleExportToExcel () {
      let data = []
      let objectKeys = []
      if (this.assignmentsTimeSheetEntriesValue.length > 0) {
        for (let client of this.assignmentsTimeSheetEntriesValue) {
          for (let assignment of client.assignmentsTimeSheetEntries) {
            let regExp = /[0-9]{2}\/[0-9]{2}/
            objectKeys = Object.keys(assignment)
            objectKeys = objectKeys.filter(key => regExp.test(key))
            let row = {
              client: client.clientName,
              consultant: assignment.consultantName
            }
            for (let date of objectKeys) {
              row[formatDate(assignment[`${date}`].date)] =
                assignment[`${date}`].hours !== null && assignment[`${date}`].hours !== undefined
                  ? parseFloat(assignment[`${date}`].hours).toFixed(2) : ''
            }
            data.push(row)
          }
        }
      }
      data.sort((a, b) => a.client.localeCompare(b.client) || a.consultant.localeCompare(b.consultant))
      if (data.length > 0) {
        let headers = Object.keys(data[0]).map(header => {
          return header.charAt(0).toUpperCase() + header.slice(1)
        })
        let columns = Object.keys(data[0])
        const params = {
          title: headers,
          key: columns,
          data: data,
          autoWidth: true,
          filename: `Time`
        }
        excel.exportArray2Excel(params)
      }
    },
    timeValueChangeHandler (item, hours, dateLabel, assignmentId, userId, staffId) {
      // If the time has changed.
      if (parseFloat(item[`${dateLabel}`].hours) !== parseFloat(hours) && hours !== null && hours !== undefined) {
        item[`${dateLabel}`].hours = parseFloat(hours)
        // Update assignment total hours
        let totalHours = 0
        // Regular expression to match MM/dd format
        const dateRegex = /^\d{2}\/\d{2}$/
        for (const key in item) {
          if (dateRegex.test(key) && typeof item[key].hours === 'number') {
            totalHours += item[key].hours
          }
        }
        item['totalTime'] = totalHours

        if (!staffId) {
          // Update client total hours
          var tempClients = this.timeManagementClientItems.filter(client => client.clientId === item.clientId)
          if (tempClients != null && tempClients.length > 0) {
            tempClients[0].totalTime = tempClients[0].assignmentsTimeSheetEntries.reduce((sum, obj) => {
              const objSum = Object.keys(obj)
                .filter(key => dateRegex.test(key) && typeof obj[key].hours === 'number')
                .map(key => obj[key].hours)
                .reduce((acc, value) => acc + value, 0)

              return sum + objSum
            }, 0)
          }
        }

        let tempTimeSheetEntryToSave = {
          assignmentId: assignmentId,
          userId: userId,
          hours: hours,
          date: item[`${dateLabel}`].date,
          dateLabel: dateLabel,
          id: item[`${dateLabel}`].timeSheetEntryId,
          staffId: staffId
        }
        let existingEntry = this.timeSheetEntriesToSave.filter(
          tse => tse.assignmentId === tempTimeSheetEntryToSave.assignmentId &&
            tse.dateLabel === tempTimeSheetEntryToSave.dateLabel
        )
        if (existingEntry && existingEntry.length > 0) {
          existingEntry[0].hours = tempTimeSheetEntryToSave.hours
        } else {
          this.timeSheetEntriesToSave.push(tempTimeSheetEntryToSave)
        }
      }
    },
    saveChanges () {
      this.saveTimeSheetEntries(this.timeSheetEntriesToSave).then(() => {
        this.loadTime(this.dateBeginFilter, this.dateEndFilter)
      })
    },
    discardChanges () {
      this.loadTime(this.dateBeginFilter, this.dateEndFilter)
    }
  }
}
</script>
